import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { dropEffect, targetOf, type DragPayload, type DropTarget } from './drop-target';

export const DRAG_THRESHOLD = 6;

type DragOptions = {
  /** What a press on this element drags (null: nothing to drag). */
  payload: () => DragPayload | null;
  /** Text of the floating preview ("Moodboard", "3 boards"). */
  label: (payload: DragPayload) => string;
  onDrop: (payload: DragPayload, target: DropTarget, effect: 'move' | 'reorder') => void;
};

/**
 * Pointer drag and drop for the dashboard (P16-4 Ruling 5): mouse and pen only (a finger scrolls; a long press opens
 * the menu), starts past 6 px, shows a preview under the pointer, marks the target it would land on, cancels on
 * Escape, and swallows the click that ends a drag so the card does not open.
 */
export function useDrag({ payload, label, onDrop }: DragOptions) {
  const latest = useRef({ payload, label, onDrop });
  latest.current = { payload, label, onDrop };

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === 'touch' || e.button !== 0 || e.shiftKey || e.metaKey || e.ctrlKey) return;
    if (e.target instanceof Element && e.target.closest('button, input, select, textarea, [role="menuitem"]')) return;
    const start = { x: e.clientX, y: e.clientY };
    let drag: DragPayload | null = null;
    let ghost: HTMLElement | null = null;
    let active: HTMLElement | null = null;
    let cancelled = false;

    const mark = (el: HTMLElement | null) => {
      if (active === el) return;
      active?.removeAttribute('data-drop-active');
      active = el;
      active?.setAttribute('data-drop-active', 'true');
    };
    const hit = (x: number, y: number) => {
      const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-drop-project]') ?? null;
      const target = targetOf(el);
      return target && drag && dropEffect(drag, target) !== 'none' ? { el: el!, target } : null;
    };
    const end = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key, true);
      ghost?.remove();
      mark(null);
      if (drag) {
        // The click that follows the release must not open the card.
        const swallow = (ev: Event) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
      }
    };
    const move = (ev: PointerEvent) => {
      if (cancelled) return;
      if (!drag) {
        if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_THRESHOLD) return;
        drag = latest.current.payload();
        if (!drag) return end();
        ghost = document.createElement('div');
        ghost.dataset.testid = 'drag-ghost';
        ghost.setAttribute('aria-hidden', 'true');
        ghost.className = 'pointer-events-none fixed z-50 max-w-60 truncate rounded-md bg-fg px-3 py-1.5 font-sans text-sm text-bg shadow-lg';
        ghost.textContent = latest.current.label(drag);
        document.body.append(ghost);
      }
      ghost!.style.left = `${ev.clientX + 12}px`;
      ghost!.style.top = `${ev.clientY + 12}px`;
      mark(hit(ev.clientX, ev.clientY)?.el ?? null);
    };
    const up = (ev: PointerEvent) => {
      const landed = drag && !cancelled ? hit(ev.clientX, ev.clientY) : null;
      const dropped = drag;
      end();
      if (dropped && landed) latest.current.onDrop(dropped, landed.target, dropEffect(dropped, landed.target) as 'move' | 'reorder');
    };
    const cancel = () => end();
    // Escape drops nothing; the release that follows still ends the drag (and its click is swallowed).
    const key = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape' || !drag || cancelled) return;
      ev.preventDefault();
      ev.stopPropagation();
      cancelled = true;
      ghost?.remove();
      mark(null);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key, true);
  };

  return { onPointerDown };
}
